/**
 * Hover/focus guidance tooltips on the edit dialog's "Attributes" and "Advanced attributes" tabs
 * (PLT-7027).
 *
 * Primer's `SegmentedControl` clones its `SegmentedControl.Button` children (to inject `onClick` /
 * `selected`), and the button doesn't forward a ref, so neither a wrapping `Tooltip` nor
 * `isNonFocusableTarget` can reach it. Instead the dialog tracks which tab is hovered or
 * keyboard-focused via props the button spreads onto its `<button>`, and a controlled `Tooltip`
 * anchors to a span inside the label (the `PointerOnlyTooltip` pattern). The guidance is also wired
 * to the button through `aria-describedby`, so screen readers announce it on focus.
 */
import { type ReactNode, useId, useRef, useState } from 'react';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { VisuallyHidden } from '@upright/ui/common/generic/components/visually-hidden';
import { ADVANCED_ATTRIBUTES_TAB_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';
import { INPUT_GUIDANCE } from '../input-guidance';
import { ProductEditDialogTab } from './dialog-tabs';

const TAB_GUIDANCE: Partial<Record<ProductEditDialogTab, string>> = {
  [ProductEditDialogTab.ATTRIBUTES]: INPUT_GUIDANCE.attributes,
  [ProductEditDialogTab.ADVANCED]: ADVANCED_ATTRIBUTES_TAB_GUIDANCE,
};

export function useTabGuidance() {
  const [activeTab, setActiveTab] = useState<ProductEditDialogTab | null>(null);
  const idPrefix = useId();

  const close = (tab: ProductEditDialogTab) => {
    setActiveTab(prev => (prev === tab ? null : prev));
  };

  // Props for the tab's `SegmentedControl.Button`; empty for tabs without guidance.
  const buttonProps = (tab: ProductEditDialogTab) =>
    TAB_GUIDANCE[tab] === undefined
      ? {}
      : {
          'aria-describedby': `${idPrefix}-${tab}`,
          onMouseEnter: () => {
            setActiveTab(tab);
          },
          onMouseLeave: () => {
            close(tab);
          },
          // Focus opens it too, for keyboard users. A mouse click also focuses the button, but the
          // pointer is already over it then, and leaving closes it again.
          onFocus: () => {
            setActiveTab(tab);
          },
          onBlur: () => {
            close(tab);
          },
        };

  // Wraps the tab's label with the anchored tooltip and the visually hidden description.
  const renderLabel = (tab: ProductEditDialogTab, label: ReactNode) => {
    const guidance = TAB_GUIDANCE[tab];
    if (guidance === undefined) return label;
    return (
      <TabLabelWithGuidance id={`${idPrefix}-${tab}`} guidance={guidance} isOpen={activeTab === tab}>
        {label}
      </TabLabelWithGuidance>
    );
  };

  return { buttonProps, renderLabel };
}

interface TabLabelWithGuidanceProps {
  id: string;
  guidance: string;
  isOpen: boolean;
  children: ReactNode;
}

function TabLabelWithGuidance({ id, guidance, isOpen, children }: TabLabelWithGuidanceProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  return (
    <>
      <Tooltip text={guidance} placement="top" isOpen={isOpen} triggerRef={anchorRef}>
        <span ref={anchorRef}>{children}</span>
      </Tooltip>
      <VisuallyHidden>
        <span id={id}>{guidance}</span>
      </VisuallyHidden>
    </>
  );
}
